import axios from 'axios'
import {format} from 'date-fns'
import {
  AvailabilityExceptionModel,
  AvailabilitySlotModel,
  ChartDataModel,
  LaborAutofillResultModel,
  EmployeeAvailabilityListItemModel,
  EmployeeAvailabilityModel,
  LaborPendingProposalModel,
  LaborProposalApplyMode,
  LaborProposalApplyResultModel,
  LaborProposalModel,
  LaborProposalSummaryModel,
  LaborShiftModel,
  LaborTemplateListItemModel,
  LaborTemplateModel,
  LaborTemplateShiftModel,
} from './_models'

// v2 FastAPI backend (fds-v2): the legacy /api/schedules + /api/employees
// namespaces both migrated onto the /labor router; /locations kept its own.
const LABOR_API_URL = process.env.REACT_APP_API_URL + '/labor'
const LOCATIONS_API_URL = process.env.REACT_APP_API_URL + '/locations'
const FORECASTING_API_URL = process.env.REACT_APP_API_URL + '/forecasting'
const SCHEDULER_CONFIGS_API_URL = process.env.REACT_APP_API_URL + '/scheduler-configs'
const EMPLOYEES_API_URL = LABOR_API_URL + '/employees'

export async function getLocations() {
  try {
    const response = await axios.get(`${LOCATIONS_API_URL}/user-scoped`)
    const groups = response.data

    if (groups && groups.length > 0) {
      return groups
    }
  } catch (error) {
    console.log(error)
  }

  return undefined
}

export async function getLocationByLocationId(location_id: number | null) {
  try {
    if (!location_id) return undefined
    const response = await axios.get(`${LOCATIONS_API_URL}/by-pos-id/${location_id}`)

    const group = response.data

    if (group) {
      return group
    }
  } catch (error) {
    console.log(error)
  }

  return undefined
}

export async function getLocationIdByGroupId(group_id: number | null) {
  try {
    if (!group_id) return undefined
    const response = await axios.get(`${LOCATIONS_API_URL}/by-pos-id/${group_id}`)

    const group = response.data

    if (group) {
      return group
    }
  } catch (error) {
    console.log(error)
  }

  return undefined
}

export async function getLaborScheduleActual(location_id: number | null, scheduleDate: Date) {
  try {
    if (!location_id) return undefined

    const response = await axios.get(`${LABOR_API_URL}/schedule`, {
      params: {
        location_id: location_id,
        schedule_date: format(scheduleDate, 'yyyy-MM-dd'),
      },
    })
    const schedules = response.data

    if (schedules) {
      return schedules
    }
  } catch (error) {
    console.log(error)
  }

  return undefined
}

export async function getLaborPositions(selectedLocationId: number | null) {
  try {
    if (!selectedLocationId) return undefined

    const response = await axios.get(`${EMPLOYEES_API_URL}/positions/${selectedLocationId}`)
    const positions = response.data['positions']

    if (positions) {
      return positions
    }
  } catch (error) {
    console.log(error)
  }

  return undefined
}

export async function getWeeklyScheduleSummary(
  selectedLocationId: number | null,
  scheduleDate: Date
) {
  try {
    if (!selectedLocationId) return undefined

    const response = await axios.get(`${LABOR_API_URL}/schedule/weekly-summary`, {
      params: {location_id: selectedLocationId, schedule_date: format(scheduleDate, 'yyyy-MM-dd')},
    })
    const schedules = response.data

    if (schedules) {
      return schedules
    }
  } catch (error) {
    console.log(error)
  }

  return undefined
}

export async function getEmployees(location_id: number | null, position: string) {
  try {
    // Legacy /api/employees/<location_id>/<position> → v2 query params.
    const url: string = `${EMPLOYEES_API_URL}/pos-location`
    const response = await axios.get(url, {
      // include_inactive: shifts on old schedules may belong to employees who
      // are inactive now — their rows are filtered client-side per week.
      params: {location_id: location_id, position: position, include_inactive: true},
    })
    const employees = response.data

    if (employees) {
      // NB: ordering is applied by the caller AFTER setPositionsMeta seeds the
      // position registry. Sorting here would run against an empty registry
      // (positions are fetched concurrently) so every sort_order resolves to
      // undefined and the order collapses to alphabetical-by-position-key.
      return employees
    }
  } catch (error) {
    console.log(error)
  }

  return undefined
}

export async function setEmployeeHideStatus(employee_id: number, hide_in_schedule: boolean) {
  try {
    const url = `${EMPLOYEES_API_URL}/${employee_id}/hide-status`

    const response = await axios.put(url, {hide_in_schedule: hide_in_schedule})

    if (response) {
      return response
    }
  } catch (error) {
    console.log(error)
  }

  return undefined
}

export async function updateLaborScheduleStatus(
  schedule_id: number,
  status: string,
  reason: string
) {
  try {
    if (!status) return undefined

    // FDS-admin force-set escape hatch — normal status changes go through
    // applyScheduleWorkflowAction. v2 takes the reason as a query param.
    const url = `${LABOR_API_URL}/schedule/${schedule_id}/status/${status}`
    const response = await axios.put(url, undefined, {params: {reason: reason}})

    if (response) {
      return response
    }
  } catch (error) {
    console.log(error)
  }

  return undefined
}

export async function getScheduleWorkflow(schedule_id: number) {
  try {
    const url = `${LABOR_API_URL}/schedule/${schedule_id}/workflow`
    const response = await axios.get(url)
    return response.data
  } catch (error) {
    console.log(error)
  }

  return undefined
}

export async function getScheduleWorkflowLog(schedule_id: number) {
  try {
    const url = `${LABOR_API_URL}/schedule/${schedule_id}/workflow-log`
    const response = await axios.get(url)
    return response.data
  } catch (error) {
    console.log(error)
  }

  return []
}

// Deliberately does NOT swallow errors: the dropdown needs the status code
// (409 conflict, 422 guard payloads) to drive its warning/refresh flows.
export async function applyScheduleWorkflowAction(
  schedule_id: number,
  action_key: string,
  body: {reason?: string; acknowledged_warnings?: string[]; expected_status?: string}
) {
  const url = `${LABOR_API_URL}/schedule/${schedule_id}/workflow-actions/${action_key}`
  const response = await axios.post(url, body)
  return response.data
}

/**
 * The break half of a shift payload.
 *
 * Both-or-neither: a half-set pair means no break, which is what the API
 * validates for anyway. Every write is a full replace, so this must be sent on
 * EVERY create and update — omitting it deletes the shift's break.
 */
const breakPayload = (shift: any) => {
  if (!shift.break_start || !shift.break_end) {
    return {break_start_time: null, break_end_time: null}
  }
  return {
    break_start_time: format(shift.break_start, 'yyyy-MM-dd HH:mm:ss'),
    break_end_time: format(shift.break_end, 'yyyy-MM-dd HH:mm:ss'),
  }
}

export const createShift = async (shift: any, business_date: Date) => {
  try {
    // console.log(shift)
    if (!shift.selectedLaborScheduleId || !shift.selectedLocationId) return undefined
    if (shift.event_id) {
      return updateShift(shift, business_date)
    }
    const payload = {
      location_id: shift.selectedLocationId,
      schedule_id: shift.selectedLaborScheduleId,
      date: format(business_date, 'yyyy-MM-dd'),
      // Only a real employee id goes to the API: '' (untouched editor select)
      // and the client-side 'unassigned-*' row sentinels both mean null.
      employee_id: typeof shift.employee_id === 'number' ? shift.employee_id : null,
      start_time: format(shift.start, 'yyyy-MM-dd HH:mm:ss'),
      end_time: format(shift.end, 'yyyy-MM-dd HH:mm:ss'),
      shift_position: shift.position || null,
      ...breakPayload(shift),
    }

    const response = await axios.post(`${LABOR_API_URL}/shifts`, payload)
    return response
  } catch (error) {
    throw error
  }
}

export const deleteShift = async (shift_id: number) => {
  try {
    const response = await axios.delete(`${LABOR_API_URL}/shifts/${shift_id}`)
    return response
  } catch (error) {
    throw error
  }
}

export const updateShift = async (shift: any, business_date: Date) => {
  try {
    if (!shift.selectedLaborScheduleId || !shift.selectedLocationId) return undefined
    // v2 addresses the shift in the path; no id field in the body.
    const payload = {
      location_id: shift.selectedLocationId,
      schedule_id: shift.selectedLaborScheduleId,
      date: format(business_date, 'yyyy-MM-dd'),
      employee_id: typeof shift.employee_id === 'number' ? shift.employee_id : null,
      start_time: format(shift.start, 'yyyy-MM-dd HH:mm:ss'),
      end_time: format(shift.end, 'yyyy-MM-dd HH:mm:ss'),
      shift_position: shift.position || null,
      ...breakPayload(shift),
    }

    const response = await axios.put(`${LABOR_API_URL}/shifts/${shift.event_id}`, payload)
    return response
  } catch (error) {
    throw error
  }
}

export const getShifts = async (start_date: Date, location_id: number) => {
  try {
    const response = await axios.get(`${LABOR_API_URL}/shifts/daily`, {
      params: {
        schedule_date: format(start_date, 'yyyy-MM-dd'),
        location_id: location_id,
      },
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const getWeatherForecast = async (location_id: number | null, schedule_date: Date) => {
  try {
    const response = await axios.get(`${LABOR_API_URL}/weather-forecast`, {
      params: {
        location_id: location_id,
        schedule_date: format(schedule_date, 'yyyy-MM-dd'),
      },
    })
    return response.data.weather
  } catch (error) {
    console.log(error)
  }
}

export const getWeeklyShifts = async (start_date: Date, location_id: number | null) => {
  try {
    const response = await axios.get(`${LABOR_API_URL}/shifts/weekly`, {
      params: {
        schedule_date: format(start_date, 'yyyy-MM-dd'),
        location_id: location_id,
      },
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const getHoursOfOperations = async (location_id: number | null) => {
  try {
    const response = await axios.get(`${LOCATIONS_API_URL}/hours-of-operations/${location_id}`)
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const getAllowableHoursDaily = async (
  location_id: number | null,
  startDate: Date,
  endDate: Date
) => {
  try {
    const response = await axios.get(`${LABOR_API_URL}/allowable-hours/${location_id}`, {
      params: {
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
      },
    })
    return response.data['allowable_hours_by_day']
  } catch (error) {
    console.log(error)
  }
}

/** Measure-derived min/max headcount per slot — the fallback for slots the
 *  allowable table has no figure for. Keyed the same way (date → HH:mm:ss). */
export const getHeadcountBoundsDaily = async (
  location_id: number | null,
  startDate: Date,
  endDate: Date
) => {
  try {
    const response = await axios.get(`${LABOR_API_URL}/headcount-bounds/${location_id}`, {
      params: {
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
      },
    })
    return response.data['headcount_by_day']
  } catch (error) {
    console.log(error)
  }
}

export const getScheduleDaySlotStats = async (location_id: number | null, day_date: Date) => {
  try {
    const response = await axios.get(`${FORECASTING_API_URL}/day-slot-stats`, {
      params: {
        location_id: location_id,
        forecast_date: format(day_date, 'yyyy-MM-dd'),
        granularity: 'minutes_15',
      },
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

// Config-driven data for a day-granularity scheduler chart (Overview tab).
// The chart config (managed in the fds admin app) decides which series come
// back; derived series are listed in `derived_series` for the client to
// compute from the live shifts.
export const getChartData = async (
  slug: string,
  location_id: number | null,
  day_date: Date
): Promise<ChartDataModel | undefined> => {
  try {
    const response = await axios.get(`${SCHEDULER_CONFIGS_API_URL}/charts/${slug}/data`, {
      params: {
        location_id: location_id,
        date: format(day_date, 'yyyy-MM-dd'),
      },
    })
    return response.data as ChartDataModel
  } catch (error) {
    console.log(error)
  }
}

// Throws on failure: the caller only clears the board once the server has.
export const clearLaborSchedule = async (location_id: number | null, schedule_date: string) => {
  const response = await axios.delete(`${LABOR_API_URL}/schedule/shifts`, {
    params: {
      location_id: location_id,
      schedule_date: schedule_date,
    },
  })
  return response.data
}

// Unassign every shift of the week (undo of the template assignment phase):
// shifts, schedule status and the template-application record stay.
export const clearScheduleAssignments = async (
  location_id: number | null,
  schedule_date: string
) => {
  try {
    const response = await axios.delete(`${LABOR_API_URL}/schedule/assignments`, {
      params: {
        location_id: location_id,
        schedule_date: schedule_date,
      },
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

/**
 * Staff every unassigned shift in the schedule's week.
 *
 * Deliberately NOT wrapped in a try/catch like most helpers here: the caller
 * has to distinguish 423 (week locked) from a generic failure, and a swallowed
 * error would surface as "0 shifts filled" — indistinguishable from a week
 * that genuinely had nothing fillable.
 */
export const autoFillSchedule = async (payload: {
  location_id: number
  schedule_id: number
  candidates?: number
  seed?: number
  dry_run?: boolean
}): Promise<LaborAutofillResultModel> => {
  const response = await axios.post(`${LABOR_API_URL}/autofill`, payload)
  return response.data
}

export const copyFromPreviousWeek = async (
  location_id: number | null,
  schedule_date: string,
  copy_from_date: Date
) => {
  try {
    const response = await axios.post(`${LABOR_API_URL}/schedule/copy-from-previous`, {
      location_id: location_id,
      schedule_date: schedule_date,
      copy_from_date: format(copy_from_date, 'yyyy-MM-dd'),
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const uploadFromFile = async (
  location_id: number | null,
  schedule_date: string,
  file: any
) => {
  try {
    const formData = new FormData()
    formData.append('location_id', location_id?.toString() || '0')
    formData.append('schedule_date', schedule_date)
    formData.append('file', file)
    const response = await axios.post(`${LABOR_API_URL}/schedule/upload-from-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

// Throws on failure, so the caller toasts the error instead of "downloaded".
export const getPDFContent = async (location_id: number | null, schedule_date: string) => {
  const response = await axios.get(`${LABOR_API_URL}/schedule/pdf`, {
    params: {
      location_id: location_id,
      schedule_date: schedule_date,
      request_time: new Date(), // this is to prevent caching
    },
    responseType: 'blob',
    headers: {
      Accept: 'application/pdf',
    },
  })
  const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `LaborSchedule_${location_id}_${schedule_date}.pdf`)
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Revoked on the next tick: revoking during the click can cancel the download.
  setTimeout(() => window.URL.revokeObjectURL(url), 0)
}

// --- Labor schedule templates (/labor/templates) ---------------------------
// Reads follow the log-and-return-undefined house style; mutations throw so
// callers can toast the failure (incl. 409 duplicate name).

export const getLaborTemplates = async (
  location_id: number | null
): Promise<LaborTemplateListItemModel[] | undefined> => {
  try {
    if (!location_id) return undefined
    const response = await axios.get(`${LABOR_API_URL}/templates`, {
      params: {location_id: location_id},
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const getLaborTemplate = async (uuid: string): Promise<LaborTemplateModel | undefined> => {
  try {
    const response = await axios.get(`${LABOR_API_URL}/templates/${uuid}`)
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const createLaborTemplate = async (payload: {
  location_id: number
  name: string
  template_type: 'daily' | 'weekly'
  shifts: LaborTemplateShiftModel[]
}) => {
  const response = await axios.post(`${LABOR_API_URL}/templates`, payload)
  return response.data as {success: boolean; message: string; uuid: string}
}

// Save what is already on the board as a template. The shifts are read from
// labor_shift_actual server-side rather than sent from here, so positions,
// breaks and overnight rollovers come from the schedule itself and not from
// whatever the board happens to have loaded.
//
// Throws like the other mutations: 409 = the name is taken, 422 = the target
// day/week has no shifts to save.
export const createLaborTemplateFromSchedule = async (payload: {
  location_id: number
  name: string
  template_type: 'daily' | 'weekly'
  date?: string // daily: the day to snapshot, 'yyyy-MM-dd'
  week_start?: string // weekly: the week to snapshot, 'yyyy-MM-dd'
  include_positions: boolean
}) => {
  const response = await axios.post(`${LABOR_API_URL}/templates/from-schedule`, payload)
  return response.data as {
    success: boolean
    message: string
    uuid: string
    shift_count: number
  }
}

export const updateLaborTemplate = async (
  uuid: string,
  payload: {name: string; shifts: LaborTemplateShiftModel[]}
) => {
  const response = await axios.put(`${LABOR_API_URL}/templates/${uuid}`, payload)
  return response.data as {success: boolean; message: string; uuid: string}
}

export const deleteLaborTemplate = async (uuid: string) => {
  const response = await axios.delete(`${LABOR_API_URL}/templates/${uuid}`)
  return response.data as {success: boolean; message: string}
}

export const applyLaborTemplate = async (
  uuid: string,
  payload: {
    location_id: number
    schedule_id: number
    date?: string // daily templates: 'yyyy-MM-dd' within the schedule week
    week_start?: string // weekly templates: the schedule's week start
  }
): Promise<LaborShiftModel[]> => {
  const response = await axios.post(`${LABOR_API_URL}/templates/${uuid}/apply`, payload)
  return response.data.shifts
}

// --- Employee availability (/labor/availability) ---------------------------
// Recurring weekly availability per employee. Saving is a whole-week replace,
// so there is no per-slot endpoint. Same house style as templates: reads
// log-and-return-undefined, mutations throw so callers can toast.

export const listLocationAvailability = async (
  location_id: number | null,
  includeSlots = false
): Promise<EmployeeAvailabilityListItemModel[] | undefined> => {
  try {
    if (!location_id) return undefined
    const response = await axios.get(`${LABOR_API_URL}/availability`, {
      params: {location_id: location_id, include_slots: includeSlots},
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const getEmployeeAvailability = async (
  location_id: number | null,
  employee_id: number | null
): Promise<EmployeeAvailabilityModel | undefined> => {
  try {
    if (!location_id || !employee_id) return undefined
    const response = await axios.get(`${LABOR_API_URL}/availability/${employee_id}`, {
      params: {location_id: location_id},
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const replaceEmployeeAvailability = async (
  employee_id: number,
  payload: {location_id: number; slots: AvailabilitySlotModel[]}
) => {
  const response = await axios.put(`${LABOR_API_URL}/availability/${employee_id}`, {
    location_id: payload.location_id,
    // uuid/is_all_day are server-owned; sending them back is harmless but the
    // payload reads cleaner (and stays valid) without them.
    slots: payload.slots.map((slot) => ({
      day_of_week: slot.day_of_week,
      slot_type: slot.slot_type,
      start_minute: slot.start_minute,
      end_minute: slot.end_minute,
      note: slot.note ?? null,
    })),
  })
  return response.data as {success: boolean; message: string; slot_count: number}
}

export const clearEmployeeAvailability = async (employee_id: number, location_id: number) => {
  const response = await axios.delete(`${LABOR_API_URL}/availability/${employee_id}`, {
    params: {location_id: location_id},
  })
  return response.data as {success: boolean; message: string; slot_count: number}
}

// Dated availability. Window-scoped on purpose: a save replaces the dates the
// editor was showing and leaves every other date — including history — alone.

export const listEmployeeExceptions = async (
  location_id: number | null,
  employee_id: number | null,
  date_from: string,
  date_to: string
): Promise<AvailabilityExceptionModel[] | undefined> => {
  try {
    if (!location_id) return undefined
    const response = await axios.get(`${LABOR_API_URL}/availability/exceptions`, {
      params: {
        location_id: location_id,
        // Omitted rather than null: without it the endpoint answers for the
        // whole location, which is what the board's shading pass wants.
        ...(employee_id ? {employee_id: employee_id} : {}),
        date_from: date_from,
        date_to: date_to,
      },
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const replaceEmployeeExceptions = async (
  employee_id: number,
  payload: {
    location_id: number
    date_from: string
    date_to: string
    slots: AvailabilityExceptionModel[]
  }
) => {
  const response = await axios.put(
    `${LABOR_API_URL}/availability/exceptions/${employee_id}`,
    {
      location_id: payload.location_id,
      date_from: payload.date_from,
      date_to: payload.date_to,
      // uuid/is_all_day/employee_id are server-owned.
      slots: payload.slots.map((slot) => ({
        date_key: slot.date_key,
        slot_type: slot.slot_type,
        start_minute: slot.start_minute,
        end_minute: slot.end_minute,
        note: slot.note ?? null,
      })),
    }
  )
  return response.data as {success: boolean; message: string; slot_count: number}
}

// --- Recommended schedules (/labor/proposals) ------------------------------
// The nightly heuristic scheduler's output. The backend does the filtering —
// past weeks, recommendations that don't beat the real schedule, and
// location-weeks somebody already decided on never come back from /pending —
// so the board can ask on every location change without its own bookkeeping.

// `week_of` is any date in the week on screen: each week carries its own
// recommendation. Without it the backend answers with the earliest open week.
export const getPendingProposal = async (
  location_id: number | null,
  week_of?: Date | null
): Promise<LaborPendingProposalModel | undefined> => {
  try {
    if (!location_id) return undefined
    const response = await axios.get(`${LABOR_API_URL}/proposals/pending`, {
      params: {
        location_id: location_id,
        ...(week_of ? {week_of: format(week_of, 'yyyy-MM-dd')} : {}),
      },
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const getProposals = async (
  location_id: number | null
): Promise<LaborProposalSummaryModel[] | undefined> => {
  try {
    if (!location_id) return undefined
    const response = await axios.get(`${LABOR_API_URL}/proposals`, {
      params: {location_id: location_id},
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const getProposal = async (uuid: string): Promise<LaborProposalModel | undefined> => {
  try {
    const response = await axios.get(`${LABOR_API_URL}/proposals/${uuid}`)
    return response.data
  } catch (error) {
    console.log(error)
  }
}

// Read receipt: stops the recommendation auto-opening again. Failing to record
// it only costs one extra prompt, so it stays in the log-and-continue style.
export const markProposalSeen = async (uuid: string) => {
  try {
    const response = await axios.post(`${LABOR_API_URL}/proposals/${uuid}/seen`)
    return response.data
  } catch (error) {
    console.log(error)
  }
}

export const dismissProposal = async (uuid: string, reason?: string) => {
  const response = await axios.post(`${LABOR_API_URL}/proposals/${uuid}/dismiss`, {
    reason: reason || null,
  })
  return response.data
}

export const applyProposal = async (
  uuid: string,
  mode: LaborProposalApplyMode
): Promise<LaborProposalApplyResultModel> => {
  const response = await axios.post(`${LABOR_API_URL}/proposals/${uuid}/apply`, {mode: mode})
  return response.data
}

export const validateSchedule = async (location_id: number | null, schedule_date: string) => {
  try {
    const response = await axios.get(`${LABOR_API_URL}/schedule/validate`, {
      params: {
        location_id: location_id,
        schedule_date: schedule_date,
      },
    })
    return response.data
  } catch (error) {
    console.log(error)
  }
}
